import { useEffect, useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { transformDetailImage } from '@/utils/cloudinary';

interface PreloadedImages {
  isPreloading: boolean;
  preloadedCount: number;
  totalCount: number;
}

/**
 * Preloads all variant images for a product into browser cache
 * This ensures instant image switching when changing variants
 */
export const useVariantImagePreloader = (productId: string | undefined): PreloadedImages => {
  const [isPreloading, setIsPreloading] = useState(true);
  const [preloadedCount, setPreloadedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const preloadedRef = useRef<Set<string>>(new Set());
  const hasStartedRef = useRef(false);

  useEffect(() => {
    if (!productId || hasStartedRef.current) return;
    hasStartedRef.current = true;

    const preloadImages = async () => {
      try {
        // Fetch all variant images for this product
        const { data: variantImages, error } = await supabase
          .from('variant_images')
          .select('image_url')
          .eq('product_id', productId);

        if (error) {
          console.error('Error fetching variant images for preload:', error);
          setIsPreloading(false);
          return;
        }

        // Also fetch variant image_url fields from product_variants
        const { data: variants, error: variantsError } = await supabase
          .from('product_variants')
          .select('image_url')
          .eq('product_id', productId)
          .not('image_url', 'is', null);

        if (variantsError) {
          console.error('Error fetching variant images from variants:', variantsError);
        }

        // Combine all image URLs and dedupe
        const allImages = new Set<string>();
        
        variantImages?.forEach(img => {
          if (img.image_url) allImages.add(img.image_url);
        });
        
        variants?.forEach(v => {
          if (v.image_url) allImages.add(v.image_url);
        });

        const imageUrls = Array.from(allImages);
        setTotalCount(imageUrls.length);

        if (imageUrls.length === 0) {
          setIsPreloading(false);
          return;
        }

        // Preload images in parallel with a concurrency limit
        const preloadImage = (url: string): Promise<void> => {
          return new Promise((resolve) => {
            if (preloadedRef.current.has(url)) {
              resolve();
              return;
            }

            const img = new Image();
            const transformedUrl = transformDetailImage(url);
            
            img.onload = () => {
              preloadedRef.current.add(url);
              setPreloadedCount(prev => prev + 1);
              resolve();
            };
            
            img.onerror = () => {
              // Try original URL if Cloudinary fails
              const fallbackImg = new Image();
              fallbackImg.onload = () => {
                preloadedRef.current.add(url);
                setPreloadedCount(prev => prev + 1);
                resolve();
              };
              fallbackImg.onerror = () => {
                setPreloadedCount(prev => prev + 1);
                resolve();
              };
              fallbackImg.src = url;
            };
            
            img.src = transformedUrl;
          });
        };

        // Preload all images (browser handles concurrency)
        await Promise.all(imageUrls.map(preloadImage));
        setIsPreloading(false);
      } catch (err) {
        console.error('Error in image preloading:', err);
        setIsPreloading(false);
      }
    };

    preloadImages();
  }, [productId]);

  return { isPreloading, preloadedCount, totalCount };
};
